import { Fragment, useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { ACTION_STYLE, ChangesRow } from "@/components/AuditChangesRow";
import { DateField } from "@/components/DateField";
import { trDates, formatDateTime } from "@/lib/constants";
import { PeriodPresets, RANGE_PRESETS, matchPreset } from "@/components/PeriodPresets";
const ALL = "all";
const EMPTY = { user_id: ALL, entity: ALL, action: ALL, date_from: "", date_to: "" };

const FilterSelect = ({ value, onChange, placeholder, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-44 bg-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function AuditLogPage() {
  const [open, setOpen] = useState({});
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = { page, page_size: 50 };
    Object.entries(f).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
    api.get("/audit-logs", { params }).then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail?.toString?.() || "İşlem geçmişi yüklenemedi"));
  }, [f, page]);

  const set = (k) => (v) => { setPage(1); setF((p) => ({ ...p, [k]: v })); };
  const pickPreset = (p) => { setPage(1); setF((prev) => ({ ...prev, ...p.range() })); };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <AppLayout title="İşlem Geçmişi" subtitle="Hangi kullanıcının hangi kaydı ne zaman eklediği, değiştirdiği veya sildiği">
      <div className="mb-3">
        <PeriodPresets presets={RANGE_PRESETS} active={matchPreset(RANGE_PRESETS, f)} onPick={pickPreset} testPrefix="audit" />
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="audit-filters">
        <FilterSelect value={f.user_id} onChange={set("user_id")} placeholder="Tüm kullanıcılar" testId="audit-filter-user"
          options={(data?.users || []).map((u) => ({ v: u.id, l: u.full_name || u.username }))} />
        <FilterSelect value={f.entity} onChange={set("entity")} placeholder="Tüm kayıt türleri" testId="audit-filter-entity"
          options={(data?.entities || []).map((e) => ({ v: e, l: e }))} />
        <FilterSelect value={f.action} onChange={set("action")} placeholder="Tüm işlemler" testId="audit-filter-action"
          options={["Ekleme", "Güncelleme", "Silme", "Gönderim", "Geri Yükleme"].map((a) => ({ v: a, l: a }))} />
        <DateField value={f.date_from} onChange={set("date_from")} testId="audit-filter-from" />
        <span className="hidden sm:inline text-slate-400 text-sm">–</span>
        <DateField value={f.date_to} onChange={set("date_to")} testId="audit-filter-to" />
        <Button variant="ghost" size="sm" onClick={() => { setPage(1); setF(EMPTY); }} data-testid="audit-filter-reset">Temizle</Button>
      </div>

      <Card className="border border-slate-200 shadow-sm overflow-hidden">
        <Table data-testid="audit-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Tarih / Saat</TableHead>
              <TableHead>Kullanıcı</TableHead>
              <TableHead>İşlem</TableHead>
              <TableHead>Kayıt Türü</TableHead>
              <TableHead>Kayıt</TableHead>
              <TableHead className="w-32">Değişiklik</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!data && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Yükleniyor...</TableCell></TableRow>
            )}
            {data && data.items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500" data-testid="audit-empty">Kayıt bulunamadı.</TableCell></TableRow>
            )}
            {data?.items.map((a) => (
              <Fragment key={a.id}>
              <TableRow data-testid={`audit-row-${a.id}`}>
                <TableCell className="font-mono text-xs whitespace-nowrap">{formatDateTime(a.at)}</TableCell>
                <TableCell className="whitespace-nowrap">{a.user_name}</TableCell>
                <TableCell>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${ACTION_STYLE[a.action] || ""}`}>{a.action}</span>
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm">{a.entity}</TableCell>
                <TableCell className="text-sm text-slate-700 min-w-[220px]">{trDates(a.label) || "-"}</TableCell>
                <TableCell>
                  {a.changes?.length > 0 ? (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-emerald-700"
                            onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))} data-testid={`audit-changes-toggle-${a.id}`}>
                      {open[a.id] ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
                      {a.changes.length} alan
                    </Button>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </TableCell>
              </TableRow>
              {open[a.id] && a.changes?.length > 0 && <ChangesRow changes={a.changes} />}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>

      <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
        <div data-testid="audit-total">Toplam {data?.total ?? 0} işlem</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <span data-testid="audit-page">{page} / {pages}</span>
          <Button variant="outline" size="icon" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="audit-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </AppLayout>
  );
}
